import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useAppStateChange } from '@/hooks/useAppState';

/**
 * SendingOverlay
 *
 * A full-screen "Waiting for you to finish sending..." overlay shown while the
 * user is handed off to their device's Messaging app from the share flow.
 *
 * Trigger: the share wizard dispatches a `share-wizard:sms-link` window event
 * immediately before navigating to the `sms:` link (see ShareWizardContext).
 * We show the overlay on that event so it is what the OS captures for the
 * app-switcher thumbnail and what greets the user the instant they return.
 *
 * Dismissal: we stop waiting as soon as the app returns to the foreground
 * (Capacitor `appStateChange` via useAppStateChange), with tap/Escape as a
 * safety net for environments where no background/foreground cycle occurs
 * (e.g. desktop browsers with no Messaging app).
 */

// The snowball logo rolls down a 6-row hill. Each backslash sits one column
// further right than the row above it (a diagonal slope), and three glyphs ride
// the slope one row apart — ° (top), * (middle), ❄️ (leading). Every frame each
// glyph shifts down one row (and thus one column down-slope); when ❄️ reaches
// the bottom it wraps back to the top, so the snowball cycles forever.
export const HILL_ROWS = 6;
export const HILL_FRAME_MS = 500;

// Glyph keyed by its row at frame 0, i.e. the original arrangement.
const SLOPE_GLYPHS: Record<number, string> = {
  1: '°',
  2: '*',
  3: '❄️',
};

export function buildHillFrame(frame: number): string {
  const lines: string[] = [];
  for (let row = 0; row < HILL_ROWS; row++) {
    // A glyph that starts on row `b` is on row `(b + frame) % HILL_ROWS`, so
    // this row holds the glyph whose start is `(row - frame) mod HILL_ROWS`.
    const start = (((row - frame) % HILL_ROWS) + HILL_ROWS) % HILL_ROWS;
    const glyph = SLOPE_GLYPHS[start];
    // `row` leading spaces, the slope, then the glyph one space down-slope.
    lines.push(' '.repeat(row) + '\\' + (glyph ? ' ' + glyph : ''));
  }
  return lines.join('\n');
}

const SendingOverlay: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [frame, setFrame] = useState(0);

  // Show when the share flow hands off to the Messaging app. The event also
  // fires under Cypress (the e2e suite intercepts it), so skip there to avoid
  // covering the page during share_wizard tests.
  useEffect(() => {
    const handleHandoff = () => {
      if ('Cypress' in window) return;
      setVisible(true);
    };
    window.addEventListener('share-wizard:sms-link', handleHandoff);
    return () =>
      window.removeEventListener('share-wizard:sms-link', handleHandoff);
  }, []);

  // Stop waiting once the user comes back to the app.
  useAppStateChange(isActive => {
    if (isActive) setVisible(false);
  });

  const dismiss = useCallback(() => setVisible(false), []);

  useEffect(() => {
    if (!visible) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismiss();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [visible, dismiss]);

  // Roll the snowball down the hill one row per frame while visible. Resetting
  // to frame 0 on each show starts the glyphs in their original arrangement.
  useEffect(() => {
    if (!visible) return;
    setFrame(0);
    const id = window.setInterval(() => {
      setFrame(f => (f + 1) % HILL_ROWS);
    }, HILL_FRAME_MS);
    return () => window.clearInterval(id);
  }, [visible]);

  if (!visible) return null;

  // Portal to <body> so the overlay escapes ion-app / router-outlet stacking
  // contexts and transformed page-transition ancestors — otherwise an entering
  // Ionic page can paint above it or clip its fixed positioning.
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Waiting for you to finish sending"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 99999,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '1.5rem',
        padding: '2rem',
        color: '#1c3d5a',
        background:
          'linear-gradient(180deg, #cfe8ff 0%, #eaf4ff 45%, #ffffff 100%)',
        userSelect: 'none',
        // No fade-in: the overlay must be fully opaque the instant it paints,
        // otherwise the OS snapshots it mid-fade (semi-transparent) for the
        // app-switcher and the underlying page shows through.
        opacity: 1,
      }}
    >
      <style>{`
        .sending-overlay-art {
          font-family: 'SF Mono', 'Menlo', 'Consolas', 'Liberation Mono', monospace !important;
        }
      `}</style>

      <pre
        aria-hidden="true"
        className="sending-overlay-art"
        style={{
          margin: 0,
          // Fixed character box so the centered block doesn't shift sideways
          // as glyphs move down the slope and line widths change frame to frame.
          width: '11ch',
          textAlign: 'left',
          fontSize: 'clamp(1.6rem, 9vw, 3.5rem)',
          lineHeight: 1.15,
          whiteSpace: 'pre',
          color: '#5b7fa6',
          textShadow: '0 1px 0 rgba(255, 255, 255, 0.7)',
        }}
      >
        {buildHillFrame(frame)}
      </pre>

      <p
        aria-live="polite"
        style={{
          margin: 0,
          fontSize: 'clamp(1.1rem, 5.5vw, 1.6rem)',
          fontWeight: 600,
        }}
      >
        Waiting for you to finish sending...
      </p>

      <p
        aria-hidden="true"
        style={{
          margin: 0,
          fontSize: '0.9rem',
          opacity: 0.55,
        }}
      >
        Tap anywhere to dismiss
      </p>

      {/* Transparent full-screen dismiss target: keeps the visible content
          non-interactive while giving us an accessible, keyboard-focusable
          control for tap-to-dismiss. */}
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        style={{
          position: 'absolute',
          inset: 0,
          background: 'transparent',
          border: 'none',
          padding: 0,
          margin: 0,
          cursor: 'pointer',
        }}
      />
    </div>,
    document.body
  );
};

export default SendingOverlay;
